/**
 * Email Classification Backfill Page (KAN-225)
 *
 * Admin-only page for manually classifying historical emails.
 * Restricted to ben.clark@palace.cl.
 *
 * Presents one email at a time with its metadata and body,
 * and lets the admin select an intent and submit.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../components/auth/AuthContext';
import {
  classificationBackfillService,
  INTENT_OPTIONS,
  type UnclassifiedEmail,
  type BackfillStats,
  type ClassificationBasis,
} from '../../services/api/classificationBackfillService';
import './ClassifyEmails.css';

const ALLOWED_EMAIL = 'ben.clark@palace.cl';

/** Intent families — static, never changes */
const FAMILY_GROUPS = [
  { label: 'Confirmation', intents: INTENT_OPTIONS.filter(i => i.family === 'confirmation') },
  { label: 'Informational', intents: INTENT_OPTIONS.filter(i => i.family === 'informational') },
  { label: 'Contract', intents: INTENT_OPTIONS.filter(i => i.family === 'contract') },
  { label: 'Non-binding', intents: INTENT_OPTIONS.filter(i => i.family === 'non_binding') },
  { label: 'Terminal', intents: INTENT_OPTIONS.filter(i => i.family === 'terminal') },
  { label: 'Uncertain', intents: INTENT_OPTIONS.filter(i => i.family === 'uncertain') },
];

const ClassifyEmails: React.FC = () => {
  const { user } = useAuth();

  const [email, setEmail] = useState<UnclassifiedEmail | null>(null);
  const [stats, setStats] = useState<BackfillStats | null>(null);
  const [selectedIntent, setSelectedIntent] = useState<string>('');
  const [selectedBasis, setSelectedBasis] = useState<ClassificationBasis | ''>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isAllowed = user?.email === ALLOWED_EMAIL;

  const loadNext = useCallback(async () => {
    if (!isAllowed) return;
    setLoading(true);
    setError(null);
    setSelectedIntent('');
    setSelectedBasis('');
    setNotes('');
    setSuccessMessage(null);
    try {
      const [nextEmail, currentStats] = await Promise.all([
        classificationBackfillService.getNextUnclassified(),
        classificationBackfillService.getStats(),
      ]);
      setEmail(nextEmail);
      setStats(currentStats);
    } catch (err: any) {
      setError(err.message || 'Failed to load next email');
    } finally {
      setLoading(false);
    }
  }, [isAllowed]);

  useEffect(() => {
    loadNext();
  }, [loadNext]);

  const handleSubmit = async () => {
    if (!email || !selectedIntent || !selectedBasis) return;

    setSubmitting(true);
    setError(null);
    try {
      await classificationBackfillService.classify({
        message_id: email.messageId,
        intent: selectedIntent,
        basis: selectedBasis,
        notes: notes || undefined,
      });
      setSuccessMessage(`Classified as "${selectedIntent}" (based on ${selectedBasis})`);
      setTimeout(() => loadNext(), 500);
    } catch (err: any) {
      setError(err.message || 'Failed to classify email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selectedIntent && selectedBasis && !submitting) {
      handleSubmit();
    }
  };

  // --- All hooks are above this line ---

  if (!isAllowed) {
    return (
      <div className="classify-emails-page">
        <div className="classify-emails-restricted">Access restricted.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="classify-emails-page">
        <div className="classify-emails-loading">Loading...</div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="classify-emails-page">
        <div className="classify-emails-done">
          <h2>All caught up</h2>
          <p>No more unclassified emails.</p>
          {stats && (
            <p className="classify-emails-stats">
              {stats.classified} / {stats.total} classified
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="classify-emails-page" onKeyDown={handleKeyDown} tabIndex={-1}>
      {/* Header */}
      <div className="classify-emails-header">
        <h1>Classify Emails</h1>
        {stats && (
          <div className="classify-emails-progress">
            <span className="classify-emails-progress-count">
              {stats.classified} / {stats.total}
            </span>
            <span className="classify-emails-progress-label">classified</span>
            <div className="classify-emails-progress-bar">
              <div
                className="classify-emails-progress-fill"
                style={{ width: `${stats.total > 0 ? (stats.classified / stats.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && <div className="classify-emails-error">{error}</div>}
      {successMessage && <div className="classify-emails-success">{successMessage}</div>}

      {/* Two-panel content */}
      <div className="classify-emails-content">
        {/* Left: email details */}
        <div className="classify-emails-email-panel">
          <table className="classify-emails-meta">
            <tbody>
              <tr>
                <td className="classify-emails-meta-label">Subject</td>
                <td className="classify-emails-meta-value">{email.subject}</td>
              </tr>
              <tr>
                <td className="classify-emails-meta-label">From</td>
                <td className="classify-emails-meta-value">{email.sender}</td>
              </tr>
              <tr>
                <td className="classify-emails-meta-label">Date</td>
                <td className="classify-emails-meta-value">{email.emailDate}</td>
              </tr>
              <tr>
                <td className="classify-emails-meta-label">Client</td>
                <td className="classify-emails-meta-value">{email.clientName || email.clientId}</td>
              </tr>
              <tr>
                <td className="classify-emails-meta-label">Bank</td>
                <td className="classify-emails-meta-value">{email.bankName || '(unknown)'}</td>
              </tr>
              {email.productType && (
                <tr>
                  <td className="classify-emails-meta-label">Product</td>
                  <td className="classify-emails-meta-value">{email.productType}</td>
                </tr>
              )}
              <tr>
                <td className="classify-emails-meta-label">Current type</td>
                <td className="classify-emails-meta-value">
                  <span className="classify-emails-current-type">
                    {email.emailType} ({email.classificationLayer})
                  </span>
                </td>
              </tr>
              {email.attachmentNames.length > 0 && (
                <tr>
                  <td className="classify-emails-meta-label">Attachments</td>
                  <td className="classify-emails-meta-value">
                    {email.attachmentNames.join(', ')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Email body (what the router will see) */}
          <div className="classify-emails-body">
            <div className="classify-emails-body-title">Email Body</div>
            {email.htmlBody ? (
              <div
                className="classify-emails-body-html"
                dangerouslySetInnerHTML={{ __html: email.htmlBody }}
              />
            ) : (
              <pre className="classify-emails-body-text">
                {email.body || '(no body available)'}
              </pre>
            )}
          </div>

          {/* Parsed attachment content (what the router won't see) */}
          {email.parsedContent && (
            <div className="classify-emails-body classify-emails-parsed">
              <div className="classify-emails-body-title">Parsed Attachment Content</div>
              <pre className="classify-emails-body-text">
                {email.parsedContent}
              </pre>
            </div>
          )}
        </div>

        {/* Right: classification controls */}
        <div className="classify-emails-classify-panel">
          <div className="classify-emails-panel-title">Classify Intent</div>

          <div className="classify-emails-intent-groups">
            {FAMILY_GROUPS.map(group => (
              <div key={group.label} className="classify-emails-intent-group">
                <div className="classify-emails-intent-group-label">{group.label}</div>
                {group.intents.map(intent => (
                  <button
                    key={intent.value}
                    type="button"
                    className={`classify-emails-intent-btn ${selectedIntent === intent.value ? 'selected' : ''}`}
                    onClick={() => setSelectedIntent(intent.value)}
                  >
                    {intent.label}
                  </button>
                ))}
              </div>
            ))}
          </div>

          <div className="classify-emails-basis">
            <div className="classify-emails-basis-label">Based on</div>
            <div className="classify-emails-basis-buttons">
              {([['subject', 'Subject'], ['email_body', 'Email Body'], ['attachment', 'Attachment']] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`classify-emails-basis-btn ${selectedBasis === value ? 'selected' : ''}`}
                  onClick={() => setSelectedBasis(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="classify-emails-notes">
            <label htmlFor="classify-notes">Notes (optional)</label>
            <textarea
              id="classify-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any observations about this email..."
              rows={3}
            />
          </div>

          <div className="classify-emails-actions">
            <button
              type="button"
              className="classify-emails-submit-btn"
              onClick={handleSubmit}
              disabled={!selectedIntent || !selectedBasis || submitting}
            >
              {submitting ? 'Submitting...' : 'Classify & Next'}
            </button>
            <button
              type="button"
              className="classify-emails-skip-btn"
              onClick={loadNext}
              disabled={submitting}
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassifyEmails;
